import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Filters a list in place as you type or change a dropdown.
//
// This used to call requestSubmit(). Two problems with that: the form pointed
// at a turbo-frame that doesn't exist, so Turbo fell back to a full page visit
// -- body replaced, scroll thrown back to the top -- and iOS dismisses the
// keyboard on any form submission, so the search field closed itself after a
// character or two.
//
// Fetching the turbo_stream ourselves replaces just the parts that changed.
// The fields keep their focus because nothing replaces them.
export default class extends Controller {
  static values = { delay: { type: Number, default: 300 } }

  submit() {
    this.#load()
  }

  debounceSubmit() {
    clearTimeout(this.#timeout)
    this.#timeout = setTimeout(() => this.#load(), this.delayValue)
  }

  // Enter on the phone's search key would submit the form, which reloads the
  // page and closes the keyboard. The results are already on screen.
  preventSubmit(event) {
    event.preventDefault()
    clearTimeout(this.#timeout)
    this.#load()
  }

  async #load() {
    const url = new URL(this.element.action, window.location.origin)
    new FormData(this.element).forEach((value, key) => {
      if (value) url.searchParams.set(key, value)
      else url.searchParams.delete(key)
    })

    // A slow early request must not land on top of a later one.
    const request = ++this.#request
    const response = await fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html" },
      credentials: "same-origin"
    })
    if (request !== this.#request || !response.ok) return

    Turbo.renderStreamMessage(await response.text())
    // Keep the address bar shareable, without a history entry per keystroke.
    history.replaceState(history.state, "", url)
  }

  disconnect() {
    clearTimeout(this.#timeout)
  }

  #timeout
  #request = 0
}
